import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import * as moment from 'moment';
import { RecordLine, RecordModel } from '@app/layout/record-model';
import * as g from 'src/app/globals';
import { LayoutService } from '@app/layout/layout.service';

interface ComponentPOAllocation {
  PONumber: number | null; // Null for initial stock
  QuantityAllocated: number;
}

interface ComponentAllocation {
  ComponentCode: string;
  POAllocations: ComponentPOAllocation[];
}

interface ScheduleSuggestion {
  ItemCode: string;
  Quantity: number;
  MaxQuantity: number; // New property to set maximum allowed quantity
  DeliveryDate: Date;
  IsBasedOnPO: boolean;
  PONumbers: number[] | null;
  IsBackOrder: boolean;
  ComponentsToOrder: { [key: string]: number } | null;
  ComponentAllocations: ComponentAllocation[] | null;
  UserDeliveryDate?: Date;
  showDetails?: boolean; // For toggling details
}

@Component({
  selector: 'app-schedule-suggestions',
  templateUrl: './schedule-suggestions.component.html',
  styleUrls: ['./schedule-suggestions.component.less'], // Using LESS
})
export class ScheduleSuggestionsComponent implements OnInit, OnChanges {
  @Output() okButtonClicked = new EventEmitter();
  @Output() cancelButtonClicked = new EventEmitter();

  @Input() scheduleSuggestions: ScheduleSuggestion[] = [];
  @Input() scheduleSuggestionsAll: any[] = [];
  @Input() recordLine: RecordLine;
  @Input() recordModel: RecordModel;
  @Input() line: Number;
  @Input() allData: boolean = false;

  groupedSuggestions: ScheduleSuggestion[] = [];
  isGrouped: boolean = false;

  totalInStock: number = 0;
  totalOnPurchaseOrder: number = 0;
  totalOnBackOrder: number = 0;
  quantity: any;
  description: any;
  itemCode: any;

  dateFormat = g.companyConfiguration.DateFormat;

  constructor(private layoutService: LayoutService) {}

  ngOnInit(): void {
    // Initialization logic if needed
    if (this.scheduleSuggestions && !this.allData) {
      this.calculateSummary();
    }
    if (this.scheduleSuggestionsAll && this.allData) {
      this.calculateSummaryAll();
    }

    console.log(this.scheduleSuggestions);

    if (!this.allData) {
      this.itemCode = this.recordLine.Properties.find(
        (p) => p.Name == 'ItemCode'
      ).ValueAsObject;
      this.description = this.recordLine.Properties.find(
        (p) => p.Name == 'ItemDescription'
      ).ValueAsObject;
      this.quantity = this.recordLine.Properties.find(
        (p) => p.Name == 'Quantity'
      ).ValueAsObject;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.scheduleSuggestions && this.scheduleSuggestions) {
      this.calculateSummary();
    }
  }

  calculateSummary() {
    // Initialize properties
    this.scheduleSuggestions.forEach((suggestion) => {
      suggestion.DeliveryDate = this.parseDate(suggestion.DeliveryDate);
      suggestion.UserDeliveryDate = new Date(suggestion.DeliveryDate);
      suggestion.showDetails = false;
      suggestion.MaxQuantity = suggestion.Quantity; // Set MaxQuantity to initial Quantity
    });

    // Calculate initial totals
    this.calculateTotals();
  }

  calculateSummaryAll() {
    // Initialize properties
    this.scheduleSuggestionsAll.forEach((suggestions) => {
      suggestions.forEach((suggestion) => {
        suggestion.DeliveryDate = this.parseDate(suggestion.DeliveryDate);
        suggestion.UserDeliveryDate = new Date(suggestion.DeliveryDate);
        suggestion.showDetails = false;
        suggestion.MaxQuantity = suggestion.Quantity; // Set MaxQuantity to initial Quantity
      });
    });
  }

  toggleGrouping(event): void {
    //this.isGrouped = !this.isGrouped;
    if (event) {
      this.groupedSuggestions = this.groupSuggestions(this.scheduleSuggestions);
      //this.scheduleSuggestions = this.groupedSuggestions;
    } /*else {
      this.GetDeliveryScheduleSuggestion();
    }*/
    // Recalculate totals after grouping or ungrouping
    this.calculateTotals();
  }

  groupSuggestions(suggestions: ScheduleSuggestion[]): ScheduleSuggestion[] {
    // Group all suggestions into one with the latest delivery date
    const latestDate = suggestions.reduce((latest, current) => {
      return current.DeliveryDate > latest ? current.DeliveryDate : latest;
    }, suggestions[0].DeliveryDate);

    const totalQuantity = suggestions.reduce(
      (total, current) => total + (current.Quantity || 0),
      0
    );

    // Collect unique PO numbers
    const poNumbersSet = new Set<number>();
    suggestions.forEach((s) => {
      if (s.PONumbers) {
        s.PONumbers.forEach((poNumber) => poNumbersSet.add(poNumber));
      }
    });

    return [
      {
        ItemCode: suggestions[0].ItemCode,
        Quantity: totalQuantity,
        MaxQuantity: totalQuantity, // Set MaxQuantity to totalQuantity
        DeliveryDate: latestDate,
        IsBasedOnPO: suggestions.some((s) => s.IsBasedOnPO),
        PONumbers: Array.from(poNumbersSet),
        IsBackOrder: suggestions.some((s) => s.IsBackOrder),
        ComponentsToOrder: null,
        ComponentAllocations: null,
        UserDeliveryDate: latestDate,
        showDetails: false,
      },
    ];
  }

  onDateChange(suggestion: ScheduleSuggestion, event: Date): void {
    suggestion.UserDeliveryDate = event;
  }

  onQuantityChange(): void {
    const suggestions = this.isGrouped
      ? this.groupedSuggestions
      : this.scheduleSuggestions;
    suggestions.forEach((suggestion) => {
      if (suggestion.Quantity < 0 || isNaN(suggestion.Quantity)) {
        suggestion.Quantity = 0;
      } else if (suggestion.Quantity > suggestion.MaxQuantity) {
        suggestion.Quantity = suggestion.MaxQuantity;
      }
    });
    this.calculateTotals();
  }

  calculateTotals(): void {
    this.totalInStock = 0;
    this.totalOnPurchaseOrder = 0;
    this.totalOnBackOrder = 0;

    const suggestions = this.isGrouped
      ? this.groupedSuggestions
      : this.scheduleSuggestions;

    suggestions.forEach((suggestion) => {
      const qty = suggestion.Quantity || 0;

      if (suggestion.IsBackOrder) {
        this.totalOnBackOrder += qty;
      } else if (suggestion.IsBasedOnPO) {
        this.totalOnPurchaseOrder += qty;
      } else {
        this.totalInStock += qty;
      }
    });
  }

  getStatus(suggestion: ScheduleSuggestion): string {
    if (suggestion.IsBackOrder) {
      return 'backorder';
    } else if (suggestion.IsBasedOnPO) {
      return 'po';
    } else {
      return 'stock';
    }
  }

  disabledDate = (current: Date): boolean => {
    // Disable dates before today
    return current && current < new Date();
  };

  getComponentsToOrderArray(componentsToOrder: {
    [key: string]: number;
  }): Array<{ ComponentCode: string; Quantity: number }> {
    return Object.keys(componentsToOrder).map((key) => ({
      ComponentCode: key,
      Quantity: componentsToOrder[key],
    }));
  }

  private parseDate(input: any): Date {
    if (input instanceof Date) {
      return input;
    } else if (typeof input === 'string') {
      // Assuming the format is "YYYY-MM-DDTHH:mm:ss.sssZ" or "/Date(1727333484713)/"
      const isoDate = Date.parse(input);
      if (!isNaN(isoDate)) {
        return new Date(isoDate);
      }
      const ticks = parseInt(input.match(/\d+/)[0], 10);
      return new Date(ticks);
    } else {
      return new Date(input);
    }
  }

  emitCancelButton() {
    this.cancelButtonClicked.emit();
  }

  emitOkButton() {
    if(this.isGrouped){
      this.scheduleSuggestions = this.groupedSuggestions;
    }
    this.okButtonClicked.emit();
  }

  emitReButton() {
    this.GetDeliveryScheduleSuggestion();
  }

  GetDeliveryScheduleSuggestion() {
    let component = this;
    let recordModel = this.recordModel;

    let lineCat = recordModel.Categories.find((x) => x.Id === 'Document_Lines');
    let headerCat = recordModel.Categories.find((x) => x.Id === 'Documents');

    let recordLineOfField = lineCat.Lines.find((l) => l.Id === this.line);

    let docEntryProp = lineCat.Lines[0].Properties.find(
      (p) => p.Name === 'DocEntry'
    );

    let lineNumProp = recordLineOfField.Properties.find(
      (p) => p.Name === 'LineNum'
    );

    let itemCodeProp = recordLineOfField.Properties.find(
      (p) => p.Name === 'ItemCode'
    );
    let itemCode = itemCodeProp ? itemCodeProp.ValueAsObject : null;
    if (itemCode == null || itemCode === undefined || itemCode === '') {
      return;
    }

    // Get Quantity from the current line
    let quantityProp = recordLineOfField.Properties.find(
      (p) => p.Name === 'Quantity'
    );
    let quantity = quantityProp ? quantityProp.ValueAsObject : 0;

    // Get the UDF for delivery schedule
    let deliverProp = recordLineOfField.Properties.find(
      (p) => p.Name === 'U_ITM_DELIVER_SCHEDULE'
    );

    let treeTypeProp = recordLineOfField.Properties.find(
      (p) => p.Name === 'TreeType'
    );
    let treeType = treeTypeProp ? treeTypeProp.ValueAsObject : null;

    //debugger;
    // Get Requested Delivery Date from the current line
    let shipDateProp = recordLineOfField.Properties.find(
      (p) => p.Name === 'ShipDate' || p.Name === 'DeliveryDate'
    );

    /*let shipDatePropHeader = recordLineOfField.Properties.find(
        (p) => p.Name === 'ShipDate' || p.Name === 'DeliveryDate'
      );*/

    let headerDueDate = headerCat.Lines[0].Properties.find(
      (p) => p.Name === 'DocDueDate'
    ).ValueAsObject;

    let requestedDeliveryDate = shipDateProp
      ? shipDateProp.ValueAsObject
      : null;
    if (requestedDeliveryDate == '' || undefined) {
      requestedDeliveryDate = headerCat.Lines[0].Properties.find(
        (p) => p.Name === 'DocDueDate'
      ).ValueAsObject;
    }

    if (new Date(headerDueDate) < new Date()) {
      requestedDeliveryDate = new Date();
    } else if (new Date(headerDueDate) >= new Date()) {
      requestedDeliveryDate = new Date(headerDueDate);
    }

    //console.log(treeType);

    if (treeType == 'I' || treeType == 'iIngredient') {
      deliverProp.ValueAsObject = '';
      return;
    }

    // Build additionalCommittedQuantities from other lines (excluding current line)
    let additionalCommittedQuantities = [];
    let lines = lineCat.Lines;

    lines.forEach(function (line) {
      if (line.Id !== component.line) {
        let lineItemCodeProp = line.Properties.find(
          (p) => p.Name === 'ItemCode'
        );
        let lineItemCode = lineItemCodeProp
          ? lineItemCodeProp.ValueAsObject
          : null;

        // Get U_ITM_DELIVER_SCHEDULE from the line
        let lineDeliverProp = line.Properties.find(
          (p) => p.Name === 'U_ITM_DELIVER_SCHEDULE'
        );
        let lineDeliverScheduleJson = lineDeliverProp
          ? lineDeliverProp.ValueAsObject
          : null;

        if (lineItemCode && lineDeliverScheduleJson) {
          try {
            // Parse the JSON from U_ITM_DELIVER_SCHEDULE
            let lineDeliverSchedule = JSON.parse(lineDeliverScheduleJson);

            // lineDeliverSchedule is expected to be an array of schedule suggestions
            lineDeliverSchedule.forEach(function (schedule) {
              // Extract Quantity and Date from the schedule
              let scheduleQuantity = schedule.Quantity;
              let scheduleDate = schedule.DeliveryDate;

              if (schedule.ComponentAllocations) {
                schedule.ComponentAllocations.forEach(function (
                  componentAllocations
                ) {
                  additionalCommittedQuantities.push({
                    ItemCode: componentAllocations.ComponentCode,
                    Quantity: componentAllocations.QuantityAllocated,
                    Date: scheduleDate,
                  });
                });
              }

              additionalCommittedQuantities.push({
                ItemCode: lineItemCode,
                Quantity: scheduleQuantity,
                Date: scheduleDate,
              });
            });
          } catch (ex) {
            console.error('Error parsing U_ITM_DELIVER_SCHEDULE JSON:', ex);
          }
        }
      }
    });

    // Exclude current line quantities
    let excludedCommittedQuantities = [];

    if (itemCode && quantity > 0 && deliverProp && deliverProp.ValueAsObject) {
      try {
        // Parse the JSON from U_ITM_DELIVER_SCHEDULE of the current line
        let deliverSchedule = JSON.parse(deliverProp.ValueAsObject);

        deliverSchedule.forEach(function (schedule) {
          let scheduleQuantity = schedule.Quantity;
          let scheduleDate = schedule.DeliveryDate;
          //shipDateProp.ValueAsObject = scheduleDate;

          if (scheduleQuantity != 0 && schedule.ComponentAllocations != null) {
            schedule.ComponentAllocations.forEach(function (
              componentAllocations
            ) {
              componentAllocations.POAllocations.forEach(function (
                poAllocations
              ) {
                excludedCommittedQuantities.push({
                  ItemCode: componentAllocations.ComponentCode,
                  Quantity: poAllocations.QuantityAllocated,
                  Date: scheduleDate,
                });
              });
            });

            excludedCommittedQuantities.push({
              ItemCode: itemCode,
              Quantity: scheduleQuantity,
              Date: scheduleDate,
            });
          }
        });
      } catch (ex) {
        console.error(
          'Error parsing U_ITM_DELIVER_SCHEDULE JSON for current line:',
          ex
        );
      }
    }

    // Build the payload
    let payload = {
      ItemCode: itemCode,
      Quantity: quantity,
      LineNum: lineNumProp.ValueAsObject,
      DocEntry: docEntryProp.ValueAsObject,
      RequestedDeliveryDate: (function () {
        let date = new Date(requestedDeliveryDate);
        // Get the date components (Year, Month, Day)
        let year = date.getFullYear();
        let month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based, so adding 1
        let day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      })(),
      AdditionalCommittedQuantities: additionalCommittedQuantities,
      ExcludedCommittedQuantities: excludedCommittedQuantities,
    };

    let jsonToPush = JSON.stringify(payload);
    let actionCode = 'GetDeliveryScheduleSuggestion';

    // Make the AJAX request to the backend
    this.layoutService
      .pushDataToBackend(jsonToPush, actionCode, jsonToPush)
      .subscribe((data: any) => {
        //console.log('data', data);
        if (!data.Has_Error) {
          deliverProp.ValueAsObject = data.Resault_Value;

          component.scheduleSuggestions = JSON.parse(data.Resault_Value);

          var deliverSchedule = JSON.parse(deliverProp.ValueAsObject);
          deliverSchedule.forEach(function (schedule) {
            var scheduleDate = schedule.DeliveryDate;
            shipDateProp.ValueAsObject = scheduleDate;

            if (
              recordLineOfField.Properties.find(
                (p) => p.Name === 'U_ITM_CHANGED'
              ) != null &&
              recordLineOfField.Properties.find(
                (p) => p.Name === 'U_ITM_CHANGED'
              ) !== undefined
            ) {
              recordLineOfField.Properties.find(
                (p) => p.Name === 'U_ITM_CHANGED'
              ).Value = 'Y';
              recordLineOfField.Properties.find(
                (p) => p.Name === 'U_ITM_CHANGED'
              ).ValueAsObject = 'Y';
            }

            return;
          });
        } else {
          deliverProp.ValueAsObject = '';
        }
      });
  }
}
